import { _, helpers, hooks, mui, React, ts, ui } from '_core';

import { DraftParams, Params } from './types';
import {
  checkForErrors,
  getBucketOrder,
  getOrderedHistories,
  getTitleByStatistics,
  portanFormat,
} from '../../common/utils/attribution-utils';

type ChartProps = {
  id: string;
  readOnly: boolean;
  widgetKey: string;
  params: Params | DraftParams;
  file: string;
  fullScreen: boolean;
  histories: ts.enums.HISTORY_ENUM_EXTENDED[];
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;

  startDate: string;
  endDate: string;

  riskModelIndustries?: ts.types.riskModel.CategoryFactor[];
};

const Chart = ({
  id,
  readOnly,
  loadWidgetData,
  fullScreen,
  file,
  params,
  histories,
  startDate,
  endDate,
  riskModelIndustries = [],
}: ChartProps) => {
  const uiStyles = hooks.useUiStyles();

  const [data, setData] = React.useState<ts.types.widgets.TableData>(null);
  const [error, setError] = React.useState<ts.types.common.ApiError>(null);

  const [selectedStat, setSelectedStat] = React.useState(ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM.RETURNS);
  const [selectedType, setSelectedType] = React.useState('selected_buckets');
  const [bucketOrder, setBucketOrder] = React.useState<string[]>([]);

  const orderedHistories = React.useMemo(() => getOrderedHistories(histories), [histories]);

  const [selectedHistory, setSelectedHistory] = React.useState(
    orderedHistories[0] || ts.enums.HISTORY_ENUM_EXTENDED.FULL
  );

  const getHistLabel = (h: (typeof histories)[0]) => {
    return helpers.periods.isAnnualized(startDate, endDate, h) ? `${h} (Annualized)` : h;
  };

  const loadData = async (bucketOrder: string[]) => {
    const statisticsType = selectedStat || ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM.RETURNS;
    try {
      const response = await loadWidgetData({
        data: {
          file,
          query: [
            '$and',
            ['order', 'equals', `['${bucketOrder.join("', '")}']`],
            ['statistics_type', 'equals', statisticsType],
            ['$or', ['history', 'equals', selectedHistory], ['history', 'equals', ts.enums.HISTORY_ENUM_EXTENDED.FULL]],
          ],
          rename: { build_date: 'date' },
          columns: [...bucketOrder, 'holdings', 'returns', 'history'],
        },
      });
      const error = checkForErrors(response.data, 'attributed_returns');
      if (!error) {
        const responseData = portanFormat(response.data, bucketOrder, selectedHistory, riskModelIndustries);

        setData(responseData);
      } else {
        setError({ responseJSON: { detail: error } });
      }
    } catch (err) {
      setError(err as ts.types.common.ApiError);
    }
  };

  React.useEffect(() => {
    // In legacy jobs, buckets and variables may be arrays with exactly one element.
    const buckets = _.isArray(params.buckets) ? params.buckets[0] : params.buckets;
    const variables = _.isArray(params.variables) ? params.variables[0] : params.variables;
    const bucketOrder = getBucketOrder({ buckets: buckets, variables: variables }, selectedType);
    setBucketOrder(bucketOrder);

    if (file) loadData(bucketOrder);
    return () => {
      setError(null);
      setData(null);
    };
  }, [file, selectedType, selectedHistory, selectedStat]);

  const statisticsName = getTitleByStatistics(selectedStat);

  let historyName;
  if (selectedHistory !== ts.enums.HISTORY_ENUM_EXTENDED.FULL) historyName = `Last ${selectedHistory}`;
  else historyName = 'Entire Period';

  const homeText = `Returns-Driven ${statisticsName} Decomposition by Average Allocation over the ${historyName}`;

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%', background: '#fff' }}>
      <mui.core.Box px={4} sx={uiStyles.hidePrint}>
        <mui.core.Grid
          container
          spacing={2}
          style={{
            marginTop: '12px',
          }}
        >
          <mui.core.Grid size={{ xs: 12, sm: 2 }}>
            <mui.core.TextField
              select
              label="Summary Statistics"
              variant="outlined"
              required
              size="small"
              value={selectedStat}
              onChange={(e) => setSelectedStat(e.target.value as ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM)}
              fullWidth
            >
              <mui.core.MenuItem value={ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM.RETURNS}>
                Annualized Returns
              </mui.core.MenuItem>
              <mui.core.MenuItem value={ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM.RISK}>
                Annualized Risk
              </mui.core.MenuItem>
              <mui.core.MenuItem value={ts.enums.REPORT_ENUMS.ANNU_STATISTICS_ENUM.ADJUSTED}>
                Annualized Risk Adjusted Returns
              </mui.core.MenuItem>
            </mui.core.TextField>
          </mui.core.Grid>

          <mui.core.Grid size={{ xs: 12, sm: 2 }}>
            <mui.core.TextField
              select
              label="Selected History"
              variant="outlined"
              size="small"
              value={selectedHistory}
              onChange={(e) => setSelectedHistory(e.target.value as ts.enums.HISTORY_ENUM_EXTENDED)}
              fullWidth
            >
              {orderedHistories.map((h) => (
                <mui.core.MenuItem key={h} value={h}>
                  {getHistLabel(h)}
                </mui.core.MenuItem>
              ))}
            </mui.core.TextField>
          </mui.core.Grid>

          <mui.core.Grid size={{ xs: 12, sm: 4 }}>
            <mui.core.FormControl component="fieldset">
              <mui.core.RadioGroup
                row
                aria-label="position"
                name="position"
                value={selectedType}
                onChange={(e) => {
                  setSelectedType(e.target.value);
                }}
              >
                <mui.core.FormControlLabel
                  value="selected_variables"
                  control={<mui.core.Radio color="primary" />}
                  label="Show Risk Factors First"
                />
                <mui.core.FormControlLabel
                  value="selected_buckets"
                  control={<mui.core.Radio color="primary" />}
                  label="Show Buckets First"
                />
              </mui.core.RadioGroup>
            </mui.core.FormControl>
          </mui.core.Grid>
        </mui.core.Grid>
      </mui.core.Box>
      <mui.core.Box p={3} flex="1">
        <ui.DrilldownTreemap
          id={id}
          data={data}
          category={bucketOrder[0]}
          lvl1Name={bucketOrder[0]}
          lvl1Value="returns"
          lvl2Name={bucketOrder[1]}
          lvl2Value="returns"
          lvl2Label={bucketOrder[1]}
          lvl2Weight="holdings"
          fullScreen={fullScreen}
          readOnly={readOnly}
          numberFormat={selectedStat != 'adjusted' ? '#.##%' : '#.##'}
          alert={error ? { type: 'error', message: helpers.parseApiError(error) } : undefined}
          homeText={homeText}
        />
      </mui.core.Box>
    </mui.core.Box>
  );
};

export default Chart;
